// @ts-nocheck — aspirational data shapes / API mismatch; tracked under Task #75 (type-safety debt)
import { ReactNode, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home, User, LogOut, Menu, ChevronLeft, ChevronRight, FileText,
  Truck, Bell, Package, ClipboardList, Receipt, Ship, BarChart3,
  Bot, Search, ExternalLink, Wallet, Users, Gavel,
  MessageSquare, ShoppingBag, Building2
} from "lucide-react";
import { PageTransition } from "@/components/ui/page-transition";
import { isAdmin } from "@/lib/permissions";

interface PortalLayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  external?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
  platform?: string;
}

const navGroups: NavGroup[] = [
  {
    title: "Overview",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: Home },
      { path: "/inquiries", label: "My Inquiries", icon: ClipboardList },
      { path: "/tracking", label: "Tracking", icon: Truck },
    ],
  },
  {
    title: "OPT · Business",
    platform: "opt",
    items: [
      { path: "https://opt.molochain.com/dashboard/inquiries", label: "Inquiries", icon: ClipboardList, external: true },
      { path: "https://opt.molochain.com/dashboard/bookings", label: "Bookings", icon: Package, external: true },
      { path: "https://opt.molochain.com/dashboard/shipments", label: "Shipments", icon: Ship, external: true },
      { path: "https://opt.molochain.com/dashboard/invoices", label: "Invoices", icon: Receipt, external: true },
      { path: "https://opt.molochain.com/dashboard/forms", label: "Documents", icon: FileText, external: true },
      { path: "https://opt.molochain.com/dashboard/reports", label: "Reports", icon: BarChart3, external: true },
    ],
  },
  {
    title: "MoloLink · Network",
    platform: "mololink",
    items: [
      { path: "https://mololink.molochain.com/marketplace", label: "Marketplace", icon: ShoppingBag, external: true },
      { path: "https://mololink.molochain.com/auctions", label: "Auctions", icon: Gavel, external: true },
      { path: "https://mololink.molochain.com/home", label: "Feed", icon: MessageSquare, external: true },
      { path: "https://mololink.molochain.com/explorer", label: "Explorer", icon: Users, external: true },
      { path: "https://mololink.molochain.com/companies", label: "Companies", icon: Building2, external: true },
    ],
  },
  {
    title: "Wallet",
    items: [
      { path: "/wallet", label: "Cash & Tokens", icon: Wallet },
    ],
  },
];

const bottomItems: NavItem[] = [
  { path: "/ai-assistant", label: "AI Assistant", icon: Bot },
  { path: "/profile", label: "My Profile", icon: User },
];

export default function PortalLayout({ children }: PortalLayoutProps) {
  const [location, navigate] = useLocation();
  const searchString = useSearch();
  const isEmbed = new URLSearchParams(searchString).get("embed") === "true";
  const { user, logoutMutation } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isEmbed) {
    return <div className="min-h-screen bg-background">{children}</div>;
  }

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      window.location.href = "https://auth.molochain.com/login";
    } catch (error) {}
  };

  const NavLink = ({ item, collapsed = false }: { item: NavItem; collapsed?: boolean }) => {
    const isActive = !item.external && (location === item.path || location.startsWith(item.path + "/"));
    const Icon = item.icon;

    if (item.external) {
      return (
        <a href={item.path} target="_blank" rel="noopener noreferrer"
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-accent/60",
            collapsed && "justify-center px-2"
          )}>
          <Icon className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span className="text-sm truncate">{item.label}</span>}
          {!collapsed && <ExternalLink className="h-3 w-3 ml-auto opacity-50" />}
        </a>
      );
    }

    return (
      <Link href={item.path}>
        <button onClick={() => setMobileOpen(false)}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200",
            "hover:bg-accent/60",
            isActive && "bg-primary/10 text-primary font-medium",
            !isActive && "text-muted-foreground hover:text-foreground",
            collapsed && "justify-center px-2"
          )}>
          <Icon className={cn("h-4 w-4 flex-shrink-0", isActive && "text-primary")} />
          {!collapsed && <span className="text-sm truncate">{item.label}</span>}
          {!collapsed && item.badge && (
            <span className="ml-auto bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full leading-none">
              {item.badge}
            </span>
          )}
        </button>
      </Link>
    );
  };

  const PlatformBadge = ({ platform }: { platform?: string }) => {
    if (!platform) return null;
    const colors: Record<string, string> = {
      opt: "bg-blue-500/10 text-blue-600 border-blue-200",
      mololink: "bg-purple-500/10 text-purple-600 border-purple-200",
    };
    return (
      <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider", colors[platform] || "bg-muted text-muted-foreground border-border")}>
        {platform}
      </span>
    );
  };

  const SidebarContent = ({ collapsed = false }: { collapsed?: boolean }) => (
    <div className="flex flex-col h-full">
      <div className={cn("p-4 flex items-center", collapsed ? "justify-center" : "gap-3")}>
        <Link href="/">
          <picture>
            <source srcSet="/molochain-logo.webp" type="image/webp" />
            <img src="/molochain-logo.png" alt="MoloChain"
              className={cn("w-auto cursor-pointer", collapsed ? "h-7" : "h-8")} />
          </picture>
        </Link>
      </div>

      <Separator className="mx-3" />

      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-5">
          {navGroups.map((group) => (
            <div key={group.title}>
              {!collapsed && (
                <div className="flex items-center gap-2 px-3 mb-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {group.title.split(" \xb7 ")[0]}
                  </p>
                  <PlatformBadge platform={group.platform} />
                </div>
              )}
              {collapsed && group.platform && (
                <div className="flex justify-center mb-2">
                  <div className={cn(
                    "w-6 h-0.5 rounded-full",
                    group.platform === "opt" ? "bg-blue-500" : group.platform === "mololink" ? "bg-purple-500" : "bg-border"
                  )} />
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink key={item.path} item={item} collapsed={collapsed} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="px-3 pb-2">
        <Separator className="mb-3" />
        <div className="space-y-0.5">
          {bottomItems.map((item) => (
            <NavLink key={item.path} item={item} collapsed={collapsed} />
          ))}
        </div>

        {isAdmin(user?.role) && (
          <div className="mt-2">
            <a href="https://admin.molochain.com"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-all">
              <ExternalLink className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">Admin Panel</span>}
            </a>
          </div>
        )}

        <Separator className="my-3" />

        <div className={cn("flex items-center gap-3 px-2 py-2", collapsed && "justify-center")}>
          <Avatar className="h-8 w-8 flex-shrink-0">
            {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user?.username} />}
            <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-xs">
              {user?.firstName?.charAt(0) || user?.username?.charAt(0)?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.firstName || user?.username}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          )}
          {!collapsed && (
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={handleLogout}>
              <LogOut className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const allItems = navGroups.flatMap(g => g.items).concat(bottomItems);
  const currentPageLabel = allItems.find(i => !i.external && (location === i.path || location.startsWith(i.path + "/")))?.label || "Dashboard";

  return (
    <div className="h-screen flex bg-background">
      <aside className={cn(
        "hidden lg:flex flex-col border-r bg-card transition-all duration-300",
        sidebarCollapsed ? "w-[68px]" : "w-[260px]"
      )}>
        <SidebarContent collapsed={sidebarCollapsed} />
        <div className="px-3 pb-3">
          <Button variant="ghost" size="sm" className="w-full justify-center"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-card/50 backdrop-blur-sm flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <SidebarContent />
              </SheetContent>
            </Sheet>
            <span className="hidden sm:inline font-medium text-sm">{currentPageLabel}</span>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/tracking")}>
              <Search className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-4 w-4" />
              <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user?.username} />}
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-xs">
                      {user?.firstName?.charAt(0) || user?.username?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.firstName || user?.username}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wallet")}>
                  <Wallet className="mr-2 h-4 w-4" />Wallet
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                  <Home className="mr-2 h-4 w-4" />Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <PageTransition>{children}</PageTransition>
        </main>
      </div>
    </div>
  );
}
